import React, { useMemo } from 'react';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import {
  SectionSidebarMenu,
  type SectionSidebarMenuGroup,
} from 'src/modules/shared/components/molecules/section-menu';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';

export interface CustomFieldsSidebarFunnel {
  id: number;
  name: string;
}

export type CustomFieldsSidebarTypeFilter =
  | 'ALL'
  | 'CONTACT'
  | 'FUNNEL_CONVERSATION'
  | 'PRODUCT';

export type CustomFieldsSidebarSelection =
  | { type: 'type'; value: CustomFieldsSidebarTypeFilter }
  | { type: 'funnel'; funnelId: number };

interface CustomFieldsSidebarProps {
  funnels: CustomFieldsSidebarFunnel[];
  selection: CustomFieldsSidebarSelection;
  onSelectType: (value: CustomFieldsSidebarTypeFilter) => void;
  onSelectFunnel: (funnelId: number) => void;
}

/**
 * Renders the sidebar menu content with filter options
 */
const SidebarMenuContent: React.FC<{
  groups: SectionSidebarMenuGroup[];
  activeId: string;
  handleSelect: (id: string) => void;
}> = ({ groups, activeId, handleSelect }) => (
  <SectionSidebarMenu
    groups={groups}
    activeId={activeId}
    onSelect={handleSelect}
    variant="neutral"
    appearance="filled"
  />
);

export const CustomFieldsSidebar: React.FC<CustomFieldsSidebarProps> = ({
  funnels,
  selection,
  onSelectType,
  onSelectFunnel,
}) => {
  const TYPE_PREFIX = 'type:';
  const FUNNEL_PREFIX = 'funnel:';

  const activeId = useMemo(() => {
    if (selection.type === 'type') {
      return `${TYPE_PREFIX}${selection.value}`;
    }
    return `${FUNNEL_PREFIX}${selection.funnelId}`;
  }, [selection]);

  const groups: SectionSidebarMenuGroup[] = useMemo(
    () => [
      {
        id: 'type',
        // TODO: check when implementing i18n
        label: 'Por entidad asociada',
        items: [
          { id: `${TYPE_PREFIX}ALL`, label: 'Todos' },
          { id: `${TYPE_PREFIX}FUNNEL_CONVERSATION`, label: 'Conversación' },
          { id: `${TYPE_PREFIX}CONTACT`, label: 'Contacto' },
          { id: `${TYPE_PREFIX}PRODUCT`, label: 'Producto' },
        ],
      },
      {
        id: 'funnel',
        // TODO: check when implementing i18n
        label: 'Por embudo ↕️',
        items: funnels.map((funnel) => ({
          id: `${FUNNEL_PREFIX}${funnel.id}`,
          label: funnel.name,
        })),
      },
    ],
    [funnels]
  );

  const handleSelect = (id: string) => {
    if (id.startsWith(TYPE_PREFIX)) {
      const value = id.slice(
        TYPE_PREFIX.length
      ) as CustomFieldsSidebarTypeFilter;
      onSelectType(value);
      return;
    }
    if (id.startsWith(FUNNEL_PREFIX)) {
      const idStr = id.slice(FUNNEL_PREFIX.length);
      const funnelId = Number(idStr);
      if (!Number.isNaN(funnelId)) {
        onSelectFunnel(funnelId);
      }
    }
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-72 border-r flex-shrink-0">
        <Panel variant="neutral" appearance="ghost" className="p-3">
          <SidebarMenuContent
            groups={groups}
            activeId={activeId}
            handleSelect={handleSelect}
          />
        </Panel>
      </div>

      {/* Mobile/Tablet Collapsible Section */}
      <div className="lg:hidden border-b bg-white m-4 rounded-lg">
        <ExpandableSection
          // TODO: check when implementing i18n
          title="Filtros"
          isInitiallyExpanded={false}
          expansionControl="left-icon"
          contentClassName="max-h-80 overflow-y-auto"
          showScrollHint
        >
          <SidebarMenuContent
            groups={groups}
            activeId={activeId}
            handleSelect={handleSelect}
          />
        </ExpandableSection>
      </div>
    </>
  );
};
